// src/Hooks/useImportExcelParser.js

import * as XLSX from 'xlsx';

const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 15));

const normalizeLooseString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

export const useImportExcelParser = (stateStore) => {
  const { refs, setters, state } = stateStore;

  const handleUploadClick = () => {
    if (refs.fileInputRef.current) refs.fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setters.setCurrentFilename(file.name);
    setters.setUploadProgress({ current: 0, total: 0 });
    setters.setUserZoomLevel(null); // AMENDED: Wipe map zoom memory on new upload
    setters.setIsProcessing(true);
    setters.setProgressMessage('Reading file...');

    setTimeout(() => {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          await yieldToMain();

          const binaryString = evt.target.result;
          const workbook = XLSX.read(binaryString, { type: 'binary' });

          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

          let headerRowIndex = -1;
          for (let i = 0; i < data.length; i++) {
            const row = data[i];
            if (
              row &&
              row.some(
                (cell) =>
                  cell !== null && cell !== undefined && String(cell).trim() !== '',
              )
            ) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex !== -1) {
            const rawCols = data[headerRowIndex];

            const remainingExcelCols = rawCols
              .map((col, originalIdx) => ({
                originalIdx,
                name: col !== null && col !== undefined ? String(col).trim() : '',
                normalized: normalizeLooseString(col),
                isMapped: false,
              }))
              .filter((c) => c.name !== '');

            const orderedColumnsList = [];
            const rowObjectKeys = [];
            const uploadedTemplateMatchedCols = [];
            const addedTargetKeys = new Set();

            const activeCustomRows = state.customRows
              ? state.customRows.filter((r) => r.name && r.name !== '-')
              : [];

            activeCustomRows.forEach((cRow) => {
              const normalizedTemplateName = normalizeLooseString(cRow.name);
              const match = remainingExcelCols.find(
                (c) => c.normalized === normalizedTemplateName,
              );

              if (match) {
                match.isMapped = true;
                if (!addedTargetKeys.has(cRow.name)) {
                  orderedColumnsList.push(cRow.name);
                  rowObjectKeys.push({
                    originalIdx: match.originalIdx,
                    targetKey: cRow.name,
                  });
                  addedTargetKeys.add(cRow.name);
                  uploadedTemplateMatchedCols.push(cRow.name);
                }
              } else {
                if (!addedTargetKeys.has(cRow.name)) {
                  orderedColumnsList.push(cRow.name);
                  addedTargetKeys.add(cRow.name);
                }
              }
            });

            remainingExcelCols
              .filter((c) => !c.isMapped)
              .forEach((unassigned) => {
                let uniqueName = unassigned.name;
                let counter = 1;
                while (addedTargetKeys.has(uniqueName)) {
                  uniqueName = `${unassigned.name}_${counter}`;
                  counter++;
                }
                addedTargetKeys.add(uniqueName);

                orderedColumnsList.push(uniqueName);
                rowObjectKeys.push({
                  originalIdx: unassigned.originalIdx,
                  targetKey: uniqueName,
                });
              });

            const rows = data
              .slice(headerRowIndex + 1)
              .filter((row) =>
                row.some(
                  (cell) =>
                    cell !== null && cell !== undefined && String(cell).trim() !== '',
                ),
              );

            const titleColKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                normalizeLooseString(state.rowMappings[k]) === 'info' &&
                normalizeLooseString(state.rowSubMappings?.[k]) === 'title',
            );
            let titleColName = null;
            if (titleColKey) {
              const cRow = state.customRows?.find((r) => r.id === titleColKey);
              if (cRow && cRow.name !== '-') titleColName = cRow.name;
              else if (titleColKey.startsWith('prop_'))
                titleColName = titleColKey.substring(5);
            }
            if (!titleColName) {
              titleColName =
                orderedColumnsList.find((c) => normalizeLooseString(c) === 'title') ||
                null;
            }

            const descColKey = Object.keys(state.rowMappings || {}).find(
              (k) => normalizeLooseString(state.rowMappings[k]) === 'description',
            );
            let descColName = null;
            if (descColKey) {
              const cRow = state.customRows?.find((r) => r.id === descColKey);
              if (cRow && cRow.name !== '-') descColName = cRow.name;
              else if (descColKey.startsWith('prop_'))
                descColName = descColKey.substring(5);
            }
            if (!descColName) {
              descColName =
                orderedColumnsList.find(
                  (c) => normalizeLooseString(c) === 'description',
                ) || null;
            }

            const rteFontType = state?.rteFontType || 'Helvetica';
            const rteFontSize = state?.rteFontSize || '10pt';
            const imHeadingFontType = state?.imHeadingFontType || 'Helvetica';
            const imHeadingFontSize = state?.imHeadingFontSize || '12pt';

            const formattedData = [];
            for (let rIndex = 0; rIndex < rows.length; rIndex++) {
              if (rIndex % 50 === 0) await yieldToMain();

              const row = rows[rIndex];
              const obj = { id: `excel_row_${Date.now()}_${rIndex}` };

              rowObjectKeys.forEach(({ originalIdx, targetKey }) => {
                let cellValue = row[originalIdx] !== undefined ? row[originalIdx] : '';

                const isHtml =
                  typeof cellValue === 'string' &&
                  /<\/?(p|br|div|h[1-6]|ul|ol|li|table|blockquote|span|strong|em)[^>]*>/i.test(
                    cellValue,
                  );

                if (typeof cellValue === 'string' && !isHtml) {
                  if (targetKey === descColName) {
                    if (cellValue.includes('\n')) {
                      cellValue = cellValue
                        .replace(/[\r\n]+/g, '\n')
                        .split('\n')
                        .filter((line) => line.trim() !== '')
                        .map(
                          (line) =>
                            `<p><span style="font-family: ${rteFontType}; font-size: ${rteFontSize};">${line.trim()}</span></p>`,
                        )
                        .join('');
                    } else if (cellValue.trim() !== '') {
                      cellValue = `<p><span style="font-family: ${rteFontType}; font-size: ${rteFontSize};">${cellValue.trim()}</span></p>`;
                    }
                  } else {
                    if (cellValue.includes('\n')) {
                      cellValue = cellValue
                        .replace(/[\r\n]+/g, '\n')
                        .split('\n')
                        .filter((line) => line.trim() !== '')
                        .map((line) => `<p>${line.trim()}</p>`)
                        .join('');
                    }
                  }
                }

                obj[targetKey] = cellValue;
              });

              if (titleColName && descColName && obj[titleColName]) {
                let titleVal = String(obj[titleColName]);

                // Force Title Case unconditionally for the heading injected into the RTE Description
                titleVal = titleVal
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase());

                const titleHtml = `<p style="margin-bottom: 6pt;"><span style="font-family: ${imHeadingFontType}; font-size: ${imHeadingFontSize};"><strong>${titleVal}</strong></span></p>`;

                let descVal = obj[descColName] ? String(obj[descColName]) : '';

                obj[descColName] = `${titleHtml}${descVal}`;
              }

              // --- ROBUST CO-ORDINATE EXTRACTION FOR INITIAL GRID RENDER ---
              let latVal =
                obj['lat'] ??
                obj['Lat'] ??
                obj['LAT'] ??
                obj['latitude'] ??
                obj['Latitude'] ??
                obj['LATITUDE'];
              let lngVal =
                obj['lng'] ??
                obj['Lng'] ??
                obj['LNG'] ??
                obj['longitude'] ??
                obj['Longitude'] ??
                obj['LONGITUDE'] ??
                obj['Lon'] ??
                obj['lon'];

              if (latVal == null || lngVal == null) {
                const posKey = orderedColumnsList.find(
                  (k) =>
                    normalizeLooseString(k) === 'position(lat,lng)' ||
                    normalizeLooseString(k) === 'position' ||
                    normalizeLooseString(k) === 'coordinates',
                );
                if (posKey && obj[posKey]) {
                  const matches = String(obj[posKey]).match(/-?\d+(\.\d+)?/g);
                  if (matches && matches.length >= 2) {
                    latVal = matches[0];
                    lngVal = matches[1];
                  }
                }
              }

              if (latVal !== undefined && latVal !== null && latVal !== '') {
                const parsedLat = parseFloat(String(latVal).trim());
                if (!isNaN(parsedLat)) obj.lat = parsedLat;
              }
              if (lngVal !== undefined && lngVal !== null && lngVal !== '') {
                const parsedLng = parseFloat(String(lngVal).trim());
                if (!isNaN(parsedLng)) obj.lng = parsedLng;
              }

              formattedData.push(obj);
            }

            if (setters.setUploadedTemplateMatchedCols) {
              setters.setUploadedTemplateMatchedCols(uploadedTemplateMatchedCols);
            }

            setters.setColumns(orderedColumnsList);
            setters.setParsedData(formattedData);
            setters.setViewMode('DATA_VIEW');
            setters.setSelectedRowIds([]);
            setters.setSuccessfullyImportedIds([]);
            setters.setStagedFiles([]);

            if (formattedData.length > 0) {
              setters.setIsPhotoFlowActive(true);
            } else {
              setters.setIsPhotoFlowActive(false);
            }
            setters.setPendingMissingColsCheck(true);
          } else {
            setters.setColumns([]);
            setters.setParsedData([]);
            setters.setSelectedRowIds([]);
            setters.setSuccessfullyImportedIds([]);
            setters.setStagedFiles([]);
            if (setters.setUploadedTemplateMatchedCols) {
              setters.setUploadedTemplateMatchedCols([]);
            }
          }
        } catch (error) {
          console.error('Excel Read Error', error);
          setters.setAlertState({
            open: true,
            message: `Failed to read Excel file: ${error.message}`,
            severity: 'error',
          });
        } finally {
          setters.setIsProcessing(false);
          setters.setProgressMessage('');
        }
      };
      reader.readAsBinaryString(file);
    }, 50);

    e.target.value = null;
  };

  return {
    handleUploadClick,
    handleFileChange,
  };
};
